import { GridApi, GridReadyEvent, RowClassParams, RowStyle } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AgTable } from '../../../../components/AgTable/AgTable';
import { ICaptableDataModel } from '../../../../data-models/captable2.data-model';
import { colors } from '../../../../theme/colors';
import { isClientInvestment, summaryRowHasHoldings } from '../captable-utils';
import { CaptableGridContainer } from '../SummaryGrid/CaptableGridContainer';
import { StakeholderType } from '../SummaryGrid/CaptableSummaryGrid';
import { captableSummaryAggFuncs, ICaptableSummaryGridContext } from './CaptableAggFuncs';
import {
  applyGroupingState,
  CaptableGroupingMode,
  CaptableSummaryDefaultColDefs2,
  useCaptableSummaryColDefs2,
} from './CaptableSummaryColDefs2';
import { ICaptableSummaryRowData2, useCaptableSummaryRowData2 } from './CaptableSummaryRowData2';

const GridHeight = 'calc(100dvh - 25rem)';

export interface ICaptableSummaryGrid2Props {
  data: ICaptableDataModel;
  quickFilterText?: string;
  investmentFilter?: StakeholderType;
  groupingMode?: CaptableGroupingMode;
}

export function CaptableSummaryGrid2({
  data,
  quickFilterText,
  investmentFilter,
  groupingMode = CaptableGroupingMode.stakeholder,
}: ICaptableSummaryGrid2Props) {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const {
    rows: unfilteredData,
    totalSharesHeld,
    totalOutstandingShares,
  } = useCaptableSummaryRowData2(data ?? null, groupingMode);
  const rowData = useMemo(() => {
    if (investmentFilter) {
      return filterByStakeholderType(unfilteredData, investmentFilter);
    }
    return unfilteredData;
  }, [unfilteredData, investmentFilter]);

  const colDefs = useCaptableSummaryColDefs2();
  const { getRowStyle } = useSummaryRowStyles2();

  const gridContext = useMemo<ICaptableSummaryGridContext>(
    () => ({ totalSharesHeld, totalOutstandingShares }),
    [totalSharesHeld, totalOutstandingShares]
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
      applyGroupingState(event.api, groupingMode);
    },
    [groupingMode]
  );

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    applyGroupingState(gridApi, groupingMode);
  }, [gridApi, groupingMode]);

  const autoHeight = unfilteredData.length <= 5;

  return (
    <CaptableGridContainer style={autoHeight ? undefined : { height: GridHeight, maxHeight: GridHeight }}>
      <AgTable
        quickFilterText={quickFilterText}
        rowData={rowData}
        columnDefs={colDefs}
        rowGroupPanelShow='never'
        defaultColDef={CaptableSummaryDefaultColDefs2}
        getRowStyle={getRowStyle}
        onGridReady={onGridReady}
        domLayout={autoHeight ? 'autoHeight' : 'normal'}
        groupDefaultExpanded={0}
        grandTotalRow='top'
        context={gridContext}
        aggFuncs={captableSummaryAggFuncs}
        suppressAggFuncInHeader={true}
      />
    </CaptableGridContainer>
  );
}

export function filterByStakeholderType(
  data: ICaptableSummaryRowData2[],
  investmentFilter: StakeholderType
): ICaptableSummaryRowData2[] {
  return data.filter((row) => {
    if (investmentFilter === StakeholderType.client) {
      return isClientInvestment(row) && summaryRowHasHoldings(row);
    } else if (investmentFilter === StakeholderType.external) {
      return !isClientInvestment(row);
    }
    return true;
  });
}

function useSummaryRowStyles2() {
  const getRowStyle = useCallback(
    (params: RowClassParams<ICaptableSummaryRowData2>): RowStyle | undefined => {
      if (params.data?.fundId) {
        return { background: colors.secondary[5] as string };
      }
    },
    []
  );

  return { getRowStyle };
}
